/* eslint-disable no-param-reassign */
import { type MutationTree, type GetterTree, type ActionTree, type ActionContext } from 'vuex';
import { wordEnding } from '@shared/lib';
import { pluralize } from '@travelata-libs/utils';
import { type TouristGroup } from '../types/TouristGroup';

///touristsByRooms содержит тот же интерфейс туристов, но в виде массива. Остальные поля для обратной совместимости
export interface TouristGroupState {
    adults: number;
    ages: number[];
    infants: number;
    kids: number;
    touristsByRooms: TouristGroup[];
}

export interface TouristGroupMutations extends MutationTree<TouristGroupState> {
    setAdults(state: TouristGroupState, adults: number): void;
    setAges(state: TouristGroupState, ages: number[]): void;
    setInfants(state: TouristGroupState, infants: number): void;
    setKids(state: TouristGroupState, kids: number): void;
    setTouristsByRooms(state: TouristGroupState, touristGroups: TouristGroup[]): void;
}

export interface TouristGroupGetters extends GetterTree<TouristGroupState, any>{
    touristGroup(state: TouristGroupState): TouristGroup;
    touristGroupText(state: TouristGroupState): string;
}

export interface TouristGroupActions extends ActionTree<TouristGroupState, any> {
    updateTouristGroup(ctx: ActionContext<TouristGroupState, any>, payload: TouristGroup ): void;
}

export interface TouristGroupStore {
    state(): TouristGroupState,
    mutations: TouristGroupMutations,
    getters: TouristGroupGetters,
    actions: TouristGroupActions,
}

export const NAMESPACE = 'touristGroupStore';

export const getGroupTouristsText = (touristsGroup: TouristGroup | null, variants = ['турист', 'туриста', 'туристов']) => {
    if(!touristsGroup) return '';
    const touristGroup = touristsGroup.touristsByRooms.reduce((acc, r) => {
        acc.adults += r.adults;
        acc.kids += r.kids;
        acc.infants += r.infants;
        return acc;
    }, { adults: touristsGroup.adults, kids: touristsGroup.kids, infants: touristsGroup.infants });
    const total = touristGroup.adults + touristGroup.infants + touristGroup.kids;

    return `${pluralize(variants, total)}`
}

export default {
    namespaced: true,
    state: () => ({
        adults: 0,
        ages: [],
        infants: 0,
        kids: 0,
        touristsByRooms: [],
    }),
    getters: {
        touristGroup(state) {
            return {
                adults: state.adults,
                ages: state.ages,
                kids: state.kids,
                infants: state.infants,
                touristsByRooms: state.touristsByRooms,
            };
        },

        touristGroupText(state) {
            const { adults, kids, infants, touristsByRooms } = state;

            if(touristsByRooms?.length) return getGroupTouristsText(state);

            const adultsText = `${adults} взросл${wordEnding(adults, 'ый;ых;ых')}`;
            const kidsTotal = kids + infants;

            if (kidsTotal > 0) {
                const kidsEnding = kids > 0 ? 'ребенок;детей;детей' : 'младенец;младенца;младенцев';

                return `${adultsText} и ${kidsTotal} ${wordEnding(kidsTotal, kidsEnding)}`;
            }
            return adultsText;
        },
    },
    mutations: {
        setAdults(state, adults) {
            state.adults = adults;
        },
        setAges(state, ages) {
            state.ages = ages;
        },
        setInfants(state, infants) {
            state.infants = infants;
        },
        setKids(state, adults) {
            state.kids = adults;
        },
        setTouristsByRooms(state, touristGroups) {
            state.touristsByRooms = touristGroups;
        }
    },
    actions: {
        updateTouristGroup({ commit }, data) {
            commit('setAdults', data.adults);
            commit('setAges', data.ages);
            commit('setInfants', data.infants);
            commit('setKids', data.kids);
            commit('setTouristsByRooms', data.touristsByRooms);
        }
    }
} as TouristGroupStore;